import { createPromisifiedAction } from '../../common/actions-promisifier/create-promisified-action';
import type { ControllerConfig } from '../../common/types';

type CaptchaConfig = Pick<ControllerConfig, 'wixCodeApi' | 'platformAPIs'> & {
  reportError?: (error: Error) => void;
};

/** The host exposes this API, but the installed IWixAPI declaration omits it. */
type CaptchaAuthentication = { openCaptchaChallenge?: () => Promise<string | undefined> };

/** Matches the installed Forms viewer's challenge policy. Runs only on submission. */
export const createRequestFormCaptcha =
  ({ wixCodeApi, platformAPIs, reportError }: CaptchaConfig) =>
  async (): Promise<string | undefined> => {
    const { baseUrl, query } = wixCodeApi.location;
    const isTestRun = Boolean(
      query.viewerPlatformOverrides ||
        query.thunderboltTag ||
        query['editor-elements-override'] ||
        query.boltTag ||
        baseUrl.startsWith('https://platform-integration') ||
        baseUrl.startsWith('https://qatestformsuser') ||
        baseUrl.startsWith('https://form-sled.wixstudio.com') ||
        baseUrl.startsWith('https://form-sled.wixsite.com') ||
        wixCodeApi.window.viewMode === 'Preview',
    );
    if ((!isTestRun || query.forceChallenge) && platformAPIs.bi?.isjp) {
      const { authentication } = wixCodeApi as typeof wixCodeApi & {
        authentication?: CaptchaAuthentication;
      };
      if (typeof authentication?.openCaptchaChallenge !== 'function') {
        // Submit without a token rather than lock the visitor out; the service decides.
        reportError?.(new Error('wixCodeApi.authentication.openCaptchaChallenge is unavailable'));
        return undefined;
      }
      const token = await authentication.openCaptchaChallenge();
      if (!token) {
        throw new Error('captcha error');
      }
      return token;
    }
    return undefined;
  };

export const createRequestFormCaptchaPromisified = (config: CaptchaConfig) => {
  const requestCaptcha = createRequestFormCaptcha(config);
  return createPromisifiedAction(
    () => () => requestCaptcha(),
    (token: string | undefined) => token,
    // Errors must survive the controller/widget serialization boundary.
    (error: unknown) => (error instanceof Error ? error.message : String(error)),
  );
};
